import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseApi } from 'sustainment-component';

export enum BomUsage {
  Engineering = 0,
  Production = 1,
  Procurement = 2
}

export enum BomStatus {
  Draft = 0,
  Active = 1,
  Obsolete = 2
}

export enum LifecycleState {
  Pending = 0,
  Active = 1,
  Inactive = 2,
  Archived = 3
}

export interface BomParentItem {
  itemId: string | null;
  itemNumber: string | null;
  itemName: string | null;
  revisionCode: string | null;
  lifecycleState: LifecycleState | null;
  procurementType: string | null;
  partType: string | null;
}

export interface BomSummary {
  totalLines: number;
  subassemblies: number;
  components: number;
  phantoms: number;
}

export interface BomDetailModel {
  id: string;
  name: string;
  usage: BomUsage;
  status: BomStatus;
  effectiveDate: string | null;
  obsoleteDate: string | null;
  externalId: string | null;
  notes: string | null;
  itemRevisionId: string | null;
  parentItem: BomParentItem | null;
  summary: BomSummary;
}

export interface BomLineChildItem {
  itemId: string | null;
  itemNumber: string | null;
  itemName: string | null;
  revisionCode: string | null;
  lifecycleState: LifecycleState | null;
  procurementType: string | null;
  partType: string | null;
}

export interface BomLineItemModel {
  id: string;
  lineNumber: number;
  quantity: number;
  unitOfMeasure: string | null;
  referenceDesignator: string | null;
  findNumber: string | null;
  scrapPercent: number | null;
  isPhantom: boolean;
  effectiveDate: string | null;
  obsoleteDate: string | null;
  notes: string | null;
  externalId: string | null;
  childItem: BomLineChildItem | null;
  childBomId: string | null;
}

export interface BomOverviewItemModel {
  id: string;
  parentItemName: string;
  parentItemRevision: string;
  name: string;
  usage: BomUsage;
  status: BomStatus;
  externalId: string;
  totalLines: number;
  linesSourced: number | null;
  coveragePercentage: number | null;
  avgSuppliersPerLine: number | null;
  preferredSuppliersCount: number | null;
  estimatedTotalCost: number | null;
}

export interface BomOverviewSummaryItem {
  id: string;
  totalLines: number;
  linesSourced: number;
  coveragePercentage: number | null;
  avgSuppliersPerLine: number | null;
  preferredSuppliersCount: number | null;
  estimatedTotalCost: number | null;
}

export interface BomTreeNode {
  id: string;
  partNumber: string;
  name: string;
  revision: string | null;
  type: string;
  makeBuy: string | null;
  quantity: number;
  unitOfMeasure: string | null;
  sequence: number;
  level: number;
  isPhantom: boolean;
  children: BomTreeNode[];
}

export type CoverageStatus = 'good' | 'ok' | 'bad';

export interface ProcurementRollupSummary {
  purchasableItemCount: number;
  coverageGoodCount: number;
  coverageTotalCount: number;
  coveragePercentage: number;
  customPercentage: number;
  otsPercentage: number;
  estimatedTotalCost: number | null;
}

export interface ProcurementUsagePath {
  path: string;
  parentPartNumber: string;
  parentName: string;
  quantityPerPath: number;
  scrapFactor: number | null;
  yieldFactor: number | null;
  adjustedQuantity: number;
}

export interface ProcurementSupplierInfo {
  supplierId: string;
  supplierName: string;
  isPreferred: boolean;
  bestQuotePrice: number | null;
  lastPurchasePrice: number | null;
  leadTimeDays: number | null;
  minimumOrderQuantity: number | null;
}

export interface ProcurementRollupItem {
  itemId: string;
  itemRevisionId: string;
  partNumber: string;
  revision: string | null;
  name: string;
  itemType: string;
  sourcingStrategy: string;
  supplierCount: number;
  coverageStatus: CoverageStatus;
  requiredQuantity: number;
  unitOfMeasure: string | null;
  estimatedUnitCost: number | null;
  extendedCost: number | null;
  validTo: string | null;
  usagePaths: ProcurementUsagePath[];
  suppliers: ProcurementSupplierInfo[];
}

export interface ProcurementRollupResponse {
  summary: ProcurementRollupSummary;
  items: ProcurementRollupItem[];
}

export interface WhereUsedModel {
  bomLineId: string;
  parentBomId: string;
  parentBomName: string;
  parentItemNumber: string | null;
  parentItemName: string | null;
  parentRevision: string | null;
  bomStatus: BomStatus;
  quantityUsed: number;
}

export interface BomPagedList<T> {
  items: T[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// OData response envelope for collection queries
export interface ODataCollectionResponse<T> {
  '@odata.count'?: number;
  value: T[];
}

// OData entity shapes (PascalCase as returned by the server)
export interface ODataBomEntity {
  Id: string;
  DisplayName: string;
  Usage: BomUsage;
  Status: BomStatus;
  EffectiveDate: string | null;
  ObsoleteDate: string | null;
  ExternalId: string | null;
  Notes: string | null;
  ItemRevisionId: string | null;
  ItemRevision?: ODataItemRevisionEntity | null;
}

export interface ODataItemRevisionEntity {
  Id: string;
  Name: string | null;
  LifecycleState: LifecycleState | null;
  ItemId: string | null;
  Item?: ODataItemEntity | null;
}

export interface ODataItemEntity {
  Id: string;
  Number: string | null;
  Name: string | null;
  ProcurementType: string | null;
  PartType: string | null;
}

export interface ODataBomLineEntity {
  Id: string;
  BillOfMaterialId: string;
  LineNumber: number;
  Quantity: number;
  UnitOfMeasure: string | null;
  ReferenceDesignator: string | null;
  FindNumber: string | null;
  ScrapPercent: number | null;
  IsPhantom: boolean;
  EffectiveDate: string | null;
  ObsoleteDate: string | null;
  Notes: string | null;
  ExternalId: string | null;
  ChildBomId: string | null;
  ItemRevisionId: string | null;
  ItemRevision?: ODataItemRevisionEntity | null;
  BillOfMaterial?: ODataBomEntity | null;
}

function mapODataBomToDetail(entity: ODataBomEntity): BomDetailModel {
  const rev = entity.ItemRevision;
  const item = rev?.Item;
  return {
    id: entity.Id,
    name: entity.DisplayName,
    usage: entity.Usage,
    status: entity.Status,
    effectiveDate: entity.EffectiveDate,
    obsoleteDate: entity.ObsoleteDate,
    externalId: entity.ExternalId,
    notes: entity.Notes,
    itemRevisionId: entity.ItemRevisionId,
    parentItem: rev
      ? {
          itemId: item?.Id ?? null,
          itemNumber: item?.Number ?? null,
          itemName: item?.Name ?? null,
          revisionCode: rev.Name,
          lifecycleState: rev.LifecycleState,
          procurementType: item?.ProcurementType ?? null,
          partType: item?.PartType ?? null
        }
      : null,
    // OData does not return computed summary — default to zeroes
    summary: { totalLines: 0, subassemblies: 0, components: 0, phantoms: 0 }
  };
}

function mapODataLineToModel(entity: ODataBomLineEntity): BomLineItemModel {
  const rev = entity.ItemRevision;
  const item = rev?.Item;
  return {
    id: entity.Id,
    lineNumber: entity.LineNumber,
    quantity: entity.Quantity,
    unitOfMeasure: entity.UnitOfMeasure,
    referenceDesignator: entity.ReferenceDesignator,
    findNumber: entity.FindNumber,
    scrapPercent: entity.ScrapPercent,
    isPhantom: entity.IsPhantom,
    effectiveDate: entity.EffectiveDate,
    obsoleteDate: entity.ObsoleteDate,
    notes: entity.Notes,
    externalId: entity.ExternalId,
    childBomId: entity.ChildBomId,
    childItem: rev
      ? {
          itemId: item?.Id ?? null,
          itemNumber: item?.Number ?? null,
          itemName: item?.Name ?? null,
          revisionCode: rev.Name,
          lifecycleState: rev.LifecycleState,
          procurementType: item?.ProcurementType ?? null,
          partType: item?.PartType ?? null
        }
      : null
  };
}

@Injectable()
export class BomApi extends BaseApi {
  protected urlBase: string;
  private _relativeUrl = 'billofmaterials';
  private _odataUrl = 'odata';

  public constructor(public _http: HttpClient) {
    super(_http);
    this.urlBase = environment.projectsApiBaseUrl;
  }

  // OData — raw entity with expanded parent item; no computed summary
  public getBomDetailsOData(bomId: string): Observable<BomDetailModel> {
    const params = new HttpParams().set(
      '$expand',
      'ItemRevision($expand=Item)'
    );
    return this._http
      .get<ODataBomEntity>(
        `${this.urlBase}${this._odataUrl}/BillOfMaterials(${bomId})`,
        { params }
      )
      .pipe(map(mapODataBomToDetail));
  }

  // OData — lines filtered by BOM ID with child item details expanded
  public getBomLines(bomId: string): Observable<BomLineItemModel[]> {
    const params = new HttpParams()
      .set('$filter', `BillOfMaterialId eq ${bomId}`)
      .set('$orderby', 'LineNumber')
      .set('$expand', 'ItemRevision($expand=Item)');
    return this._http
      .get<ODataCollectionResponse<ODataBomLineEntity>>(
        `${this.urlBase}${this._odataUrl}/BillOfMaterialLines`,
        { params }
      )
      .pipe(map((res) => res.value.map(mapODataLineToModel)));
  }

  // REST only — no OData equivalent
  public getBomTree(bomId: string): Observable<BomTreeNode[]> {
    return this._http.get<BomTreeNode[]>(
      `${this.urlBase}${this._relativeUrl}/${bomId}/tree`
    );
  }

  // REST — tree composition metrics for a single BOM
  public getBomSummary(bomId: string): Observable<BomSummary> {
    return this._http.get<BomSummary>(
      `${this.urlBase}${this._relativeUrl}/${bomId}/summary`
    );
  }

  // OData — paginated BOM list for the overview page
  public getBomListOData(
    params: HttpParams
  ): Observable<ODataCollectionResponse<ODataBomEntity>> {
    return this._http.get<ODataCollectionResponse<ODataBomEntity>>(
      `${this.urlBase}${this._odataUrl}/BillOfMaterials`,
      { params }
    );
  }

  // REST — batch sourcing summary for a set of BOMs
  public getBomBatchSummary(
    ids: string[]
  ): Observable<BomOverviewSummaryItem[]> {
    return this._http.post<BomOverviewSummaryItem[]>(
      `${this.urlBase}${this._relativeUrl}/summary`,
      ids
    );
  }

  // REST — procurement rollup (flattened BUY items with sourcing data)
  public getBomProcurementRollup(
    bomId: string
  ): Observable<ProcurementRollupResponse> {
    return this._http.get<ProcurementRollupResponse>(
      `${this.urlBase}${this._relativeUrl}/${bomId}/procurement-rollup`
    );
  }

  // OData — "where used" query: find parent BOMs that reference this item revision
  public getWhereUsed(
    itemRevisionId: string
  ): Observable<WhereUsedModel[]> {
    const params = new HttpParams()
      .set('$filter', `ItemRevisionId eq ${itemRevisionId}`)
      .set(
        '$expand',
        'BillOfMaterial($expand=ItemRevision($expand=Item))'
      );
    return this._http
      .get<ODataCollectionResponse<ODataBomLineEntity>>(
        `${this.urlBase}${this._odataUrl}/BillOfMaterialLines`,
        { params }
      )
      .pipe(
        map((res) =>
          res.value.map((line) => {
            const bom = line.BillOfMaterial;
            const rev = bom?.ItemRevision;
            const item = rev?.Item;
            return {
              bomLineId: line.Id,
              parentBomId: bom?.Id ?? line.BillOfMaterialId,
              parentBomName: bom?.DisplayName ?? '—',
              parentItemNumber: item?.Number ?? null,
              parentItemName: item?.Name ?? null,
              parentRevision: rev?.Name ?? null,
              bomStatus: bom?.Status ?? BomStatus.Draft,
              quantityUsed: line.Quantity
            };
          })
        )
      );
  }

}
